import { useReactiveVar } from '@apollo/client';
import { useCallback, useMemo } from 'react';
import { graphicStandFieldsVar } from 'graphql/cache';

const useGraphicStandFields = (standId) => {
    useReactiveVar(graphicStandFieldsVar);

    const setFieldValue = useCallback((field, value) => {
        const currentData = graphicStandFieldsVar();
        const updatedData = {
            ...currentData,
            [standId]: {
                ...currentData[standId],
                [field]: value
            }
        };
        graphicStandFieldsVar(updatedData);
    }, [standId]);

    const getFieldValue = useCallback((field) => {
        const currentData = graphicStandFieldsVar();
        const currentStandFields = currentData[standId] || {};
        return currentStandFields[field] || '';
    }, [standId]);

    const clearStandFields = useCallback(() => {
        const currentData = graphicStandFieldsVar();
        const { [standId]: removed, ...rest } = currentData;
        graphicStandFieldsVar(rest);
    }, [standId]);

    const clearField = useCallback((field) => {
        const currentData = graphicStandFieldsVar();
        const updatedData = {
            ...currentData,
            [standId]: {
                ...currentData[standId],
                [field]: ''
            }
        };
        graphicStandFieldsVar(updatedData);
    }, [standId]);

    const getAllFields = useCallback(() => {
        const currentData = graphicStandFieldsVar();
        const currentStandFields = currentData[standId] || {};
        const fields = {
            hall: currentStandFields.hall || '',
            corridor: currentStandFields.corridor || '',
            stand: currentStandFields.stand || '',
            comment: currentStandFields.comment || ''
        };

        // Add block field only if it has a value
        if (currentStandFields.block && currentStandFields.block.trim()) {
            fields.block = currentStandFields.block;
        }

        return fields;
    }, [standId]);

    const areAllFieldsFilled = useCallback(() => {
        const currentData = graphicStandFieldsVar();
        const currentStandFields = currentData[standId] || {};
        // Required fields (corridor and block are optional)
        const requiredFields = {
            hall: currentStandFields.hall || '',
            stand: currentStandFields.stand || ''
        };
        return Object.values(requiredFields).every(value => value && value.trim());
    }, [standId]);

    const result = useMemo(() => ({
        setFieldValue,
        getFieldValue,
        clearStandFields,
        clearField,
        getAllFields,
        areAllFieldsFilled
    }), [setFieldValue, getFieldValue, clearStandFields, clearField, getAllFields, areAllFieldsFilled]);

    return result;
};

export default useGraphicStandFields;
